import { Logger } from '@instinet-analytics/core-lib';

type CachedResult<T> = {
    uid: string;
    time: Date;
    response: T;
};

export interface FetchedResultCacheOptions<TResult, TOptions> {
    toUid: (options: TOptions) => string;
    fetchResult: (options: TOptions) => Promise<TResult>;
    onNewResultFetched?: (options: TOptions, result: TResult) => void;
    timeoutMins: number | undefined;
    logger: Logger;
}

/*
 * Helper class to fetch objects with an identity only once
 */
export class FetchedResultCache<TResult, TOptions> {
    private cachedResults: {
        [uid: string]: CachedResult<TResult>;
    } = {};
    private inProgress: {
        [uid: string]: Promise<TResult>;
    } = {};

    private readonly logger: Logger;
    private readonly toUid: (options: TOptions) => string;
    private readonly fetchResult: (options: TOptions) => Promise<TResult>;
    private readonly timeoutMins: number | undefined;
    private readonly onNewResultFetched?: (
        options: TOptions,
        result: TResult
    ) => void;

    public constructor(options: FetchedResultCacheOptions<TResult, TOptions>) {
        this.toUid = options.toUid;
        this.fetchResult = options.fetchResult;
        this.logger = options.logger;
        this.onNewResultFetched = options.onNewResultFetched;
        this.timeoutMins = options.timeoutMins;
    }

    public async fetch(options: TOptions): Promise<TResult> {
        const uid = this.toUid(options);

        if (this.cachedResults[uid]) {
            if (!this.hasTimedOut(this.cachedResults[uid])) {
                return Promise.resolve(this.cachedResults[uid].response);
            }
        }

        if (Object.keys(this.inProgress).includes(uid)) {
            return this.inProgress[uid];
        }

        this.logger.debug('FetchedResultCache fetching', options);

        const promise = this.fetchResult(options);

        this.inProgress[uid] = promise;

        const result = await promise;

        this.logger.debug('FetchedResultCache fetched', { options, result });

        this.cachedResults[uid] = { uid, time: new Date(), response: result };

        if (this.onNewResultFetched) {
            this.onNewResultFetched(options, result);
        }

        delete this.inProgress[uid];

        return result;
    }

    public set(options: TOptions, result: TResult) {
        const uid = this.toUid(options);
        this.cachedResults[uid] = { uid, time: new Date(), response: result };
    }

    private hasTimedOut(result: CachedResult<TResult>): boolean {
        if (!this.timeoutMins) {
            return false;
        }
        const timeOutMs = this.timeoutMins * 60000;
        const expireAt = new Date().getTime() - timeOutMs;

        return result.time.getTime() < expireAt;
    }

    public hasCachedResult(options: TOptions) {
        const uid = this.toUid(options);
        return !!this.cachedResults[uid];
    }
}
